import { FormInstance, message } from 'antd';
import dayjs from 'dayjs';

import { generateHouseHoldNumberCascader } from '@/utils/generateHouseHoldNumberCascader';
import type { GroupBuying } from '@/types/database/groupBuying';
import type { ResidentAccountNumber } from '@/types/database/residentAccountNumbers';
import type { GroupBuyingOrders } from '@/types/database/groupBuyingOrders';
import type { User } from '@/types/database/users';

import type { AddOrderFormValues, GroupBuyingOrdersService } from '../types';

export const formInitialValues = {
  groupBuyingItemName: null,
  householdNumber: [],
  residentName: null,
  purchaseAmount: null,
};

export const getGroupBuyingItemsForSelectOptions = (
  groupBuyingItems: GroupBuying[]
) => {
  const groupBuyingItemsForSelectOptions = groupBuyingItems.map((item) => {
    return {
      key: item._id,
      label: item.itemName,
      value: item.itemName,
    };
  });

  return groupBuyingItemsForSelectOptions;
};

export const getHouseholdNumberForCascaderOptions = (
  residentAccountNumbers: ResidentAccountNumber[]
) => {
  const householdNumbers = residentAccountNumbers.map(
    (resident) => resident.householdNumber
  );
  const householdNumbersForCascaderOptions =
    generateHouseHoldNumberCascader(householdNumbers);
  return householdNumbersForCascaderOptions;
};

export const getSelectedItem = (
  groupBuyingItems: GroupBuying[],
  form: FormInstance<AddOrderFormValues>
) => {
  const selectedItem = groupBuyingItems.find((item) => {
    return item.itemName === form.getFieldValue('groupBuyingItemName');
  });

  return selectedItem;
};

export const getResidentsForSelectOptions = (
  form: FormInstance<AddOrderFormValues>,
  residentAccountNumbers: ResidentAccountNumber[]
) => {
  const selectedHouseholdNumber: string[] | null | undefined =
    form.getFieldValue('householdNumber');

  if (!selectedHouseholdNumber || selectedHouseholdNumber.length === 0) {
    return;
  }

  const foundResidentAccountNumber = residentAccountNumbers.find((resident) => {
    return resident.householdNumber === selectedHouseholdNumber?.join('-');
  });

  if (
    foundResidentAccountNumber?.members === undefined ||
    foundResidentAccountNumber?.members.length === 0
  ) {
    return;
  }

  const residentsForSelectOptions = foundResidentAccountNumber?.members.map(
    (member) => {
      return {
        key: member,
        label: member,
        value: member,
      };
    }
  );

  return residentsForSelectOptions;
};

export const getOrdersPurchaseAmounts = (
  currentOrders: GroupBuyingOrders[],
  selectedGroupBuyingItem: GroupBuying | null | undefined
) => {
  const ordersPurchaseAmounts = currentOrders.reduce(
    (purchasedAmounts, order) => {
      if (
        selectedGroupBuyingItem &&
        order.groupBuyingId === selectedGroupBuyingItem._id &&
        order.paymentStatus !== '已取消'
      ) {
        return purchasedAmounts + order.purchaseAmount;
      } else {
        return purchasedAmounts;
      }
    },
    0
  );

  return ordersPurchaseAmounts;
};

export const getOrderableAmountsOptions = (args: {
  maximumAmount: number;
  currentOrders: GroupBuyingOrders[];
  selectedGroupBuyingItem: GroupBuying | null | undefined;
}) => {
  const { maximumAmount, currentOrders, selectedGroupBuyingItem } = args;
  const purchasedAmounts = getOrdersPurchaseAmounts(
    currentOrders,
    selectedGroupBuyingItem
  );
  const orderableAmounts = maximumAmount - purchasedAmounts;

  const orderableAmountsOptions = Array.from(
    { length: orderableAmounts },
    (_, index) => index + 1
  ).map((number) => {
    return {
      key: number,
      label: number,
      value: number,
    };
  });

  return orderableAmountsOptions;
};

export const getOrderedUser = (users: User[], residentName: string) => {
  const orderedUser = users.find((user) => {
    return user.name === residentName;
  });

  return orderedUser;
};

export const handleSubmit = async (args: {
  values: AddOrderFormValues;
  users: User[];
  groupBuyingOrdersService: GroupBuyingOrdersService;
  selectedGroupBuyingItem: GroupBuying | null | undefined;
  updateOrders: () => void;
  closeAddOrderModal: () => void;
}) => {
  const {
    values,
    users,
    groupBuyingOrdersService,
    selectedGroupBuyingItem,
    updateOrders,
    closeAddOrderModal,
  } = args;

  const { residentName, purchaseAmount } = values;
  const user = getOrderedUser(users, residentName);

  try {
    await groupBuyingOrdersService.create({
      userId: user?._id ?? '',
      groupBuyingId: selectedGroupBuyingItem?._id,
      orderTime: dayjs().valueOf(),
      paymentType: !selectedGroupBuyingItem?.cost[0].amount
        ? selectedGroupBuyingItem?.cost[1].type
        : selectedGroupBuyingItem?.cost[0].type,
      paymentAmount: !selectedGroupBuyingItem?.cost[0].amount
        ? selectedGroupBuyingItem?.cost[1].amount
        : selectedGroupBuyingItem?.cost[0].amount,
      purchaseAmount,
      paymentStatus: '未付款',
    });
    updateOrders();
    closeAddOrderModal();
    message.success('成功新增訂單!');
  } catch (error) {
    console.error(error);
    message.error('新增訂單失敗!');
  }
};
